import React, { useState, useEffect } from 'react';
import CollectionSearch from '../components/search/CollectionSearch';
import { filterRecords } from '../utils/search';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Container, Box, CircularProgress, Alert, Button, List, ListItem, ListItemText, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Paper, Chip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncIcon from '@mui/icons-material/Sync';
import { getAccount, getCategories, getCategoryTotals, deleteAccount, getArchivedCategories, reactivateCategory, triggerTellerSync, getTellerStatus } from '../services/api';
import AddCategoryModal from '../components/AddCategoryModal';
import EditCategoryModal from '../components/EditCategoryModal';
import CategoryTransferModal from '../components/CategoryTransferModal';

function AccountDetailPage() {
  const { id } = useParams();
  const [account, setAccount] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoryTotals, setCategoryTotals] = useState([]); // New state for category totals
  const [viewMode, setViewMode] = useState('summary'); // 'summary' or 'editCategories'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
  const [isEditCategoryModalOpen, setIsEditCategoryModalOpen] = useState(false);
  const [selectedCategoryToEdit, setSelectedCategoryToEdit] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showArchivedCategories, setShowArchivedCategories] = useState(false);
  const [archivedCategories, setArchivedCategories] = useState([]);
  const [search, setSearch] = useState('');
  const visibleTotals = filterRecords(categoryTotals, search, category => [category.category_name], { rank: true });
  const visibleCategories = filterRecords(categories, search, category => [category.name], { rank: true });
  const visibleArchived = filterRecords(archivedCategories, search, category => [category.name, category.deleted_date])
    .slice().sort((left, right) => (right.deleted_date || '').localeCompare(left.deleted_date || ''));
  const [isCategoryTransferModalOpen, setIsCategoryTransferModalOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [tellerStatus, setTellerStatus] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const accountRes = await getAccount(id);
      setAccount(accountRes.data);
      const categoriesRes = await getCategories();
      setCategories(categoriesRes.data.filter(cat => cat.account_id === parseInt(id) && !cat.is_deleted));
      const categoryTotalsRes = await getCategoryTotals(id);
      setCategoryTotals(categoryTotalsRes.data);

      if (accountRes.data.account_type === 'credit' && accountRes.data.teller_account_id) {
        try {
          const statusRes = await getTellerStatus();
          const thisAccountStatus = statusRes.data.find(s => s.account_id === parseInt(id));
          if (thisAccountStatus) {
            setTellerStatus(thisAccountStatus);
          }
        } catch (statusErr) {
          console.error('Failed to fetch Teller status', statusErr);
        }
      }
    } catch (err) {
      console.error("Failed to fetch account details or categories", err);
      setError(err.response?.data?.msg || 'Failed to load account details.');
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedCategories = async () => {
    try {
      const archivedRes = await getArchivedCategories(id);
      setArchivedCategories(archivedRes.data);
    } catch (err) {
      console.error("Failed to fetch archived categories", err);
    }
  };

  const handleToggleArchivedCategories = async () => {
    if (!showArchivedCategories) {
      await fetchArchivedCategories();
    }
    setShowArchivedCategories(!showArchivedCategories);
  };

  const handleReactivateCategory = async (categoryId, categoryName) => {
    try {
      await reactivateCategory(categoryId);
      alert(`Category "${categoryName}" has been reactivated successfully!`);
      // Refresh both active and archived categories
      fetchData();
      fetchArchivedCategories();
    } catch (err) {
      console.error("Failed to reactivate category", err);
      alert(err.response?.data?.msg || 'Failed to reactivate category');
    }
  };

  useEffect(() => {
    fetchData();
    const reload = () => fetchData();
    window.addEventListener('accounting:transactions-changed', reload);
    return () => window.removeEventListener('accounting:transactions-changed', reload);
  }, [id]);

  const handleOpenAddCategoryModal = () => setIsAddCategoryModalOpen(true);
  const handleCloseAddCategoryModal = () => setIsAddCategoryModalOpen(false);

  const handleCategoryAdded = () => {
    fetchData(); // Refresh data after a new category is added
  };

  const handleOpenEditCategoryModal = (category) => {
    setSelectedCategoryToEdit(category);
    setIsEditCategoryModalOpen(true);
  };

  const handleCloseEditCategoryModal = () => {
    setIsEditCategoryModalOpen(false);
    setSelectedCategoryToEdit(null);
  };

  const handleCategoryUpdated = () => {
    fetchData(); // Refresh data after category is updated
  };

  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setDeleteError('');
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteError('');
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    setDeleteError('');
    
    try {
      const response = await deleteAccount(id);
      const action = response.data.action;
      
      // Show success message and redirect
      if (action === 'deleted') {
        alert(`Account "${account.name}" was permanently deleted (no transaction history).`);
      } else if (action === 'archived') {
        alert(`Account "${account.name}" was archived (all balances at zero).`);
      }
      
      navigate('/'); // Redirect to dashboard
    } catch (err) {
      console.error("Failed to delete account", err);
      setDeleteError(err.response?.data?.msg || 'Failed to delete account');
      
      // If there are non-zero categories, show them
      if (err.response?.data?.non_zero_categories) {
        const categories = err.response.data.non_zero_categories
          .map(cat => `${cat.name}: $${cat.balance.toFixed(2)}`)
          .join('\n');
        setDeleteError(`${err.response.data.msg}\n\nCategories with balances:\n${categories}`);
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await triggerTellerSync();
      setSyncResult(res.data);
      fetchData();
    } catch (err) {
      console.error('Teller sync failed', err);
      setSyncResult({ error: err.response?.data?.msg || 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!account) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography variant="h6">Account not found.</Typography>
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ my: 4 }}>
        <Button variant="outlined" onClick={handleBack} sx={{ mb: 2 }}>
          Back to Dashboard
        </Button>
        <Typography variant="h4" component="h1" gutterBottom>
          Account: {account.name}
        </Typography>
        <Typography variant="body1" gutterBottom>
          ID: {account.id}
        </Typography>
        <Typography variant="body1" gutterBottom>
          Status: {account.is_active ? 'Active' : 'Inactive'}
        </Typography>
        <Typography variant="h6" component="h3" gutterBottom>
          Total Balance: ${account.total_balance}
        </Typography>

        {account.account_type === 'credit' && (
          <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Teller.io Linked Account
                </Typography>
                {tellerStatus && (
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {tellerStatus.teller_balance && (
                      <Chip
                        label={`Available: $${typeof tellerStatus.teller_balance === 'object'
                          ? tellerStatus.teller_balance.available || tellerStatus.teller_balance.current || 'N/A'
                          : tellerStatus.teller_balance}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    <Chip
                      label={`${tellerStatus.synced_transaction_count} synced`}
                      size="small"
                      variant="outlined"
                    />
                    {tellerStatus.latest_transaction_date && (
                      <Chip
                        label={`Latest: ${tellerStatus.latest_transaction_date}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                )}
              </Box>
              <Button
                variant="contained"
                startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                onClick={handleSyncNow}
                disabled={syncing}
              >
                {syncing ? 'Syncing...' : 'Sync Now'}
              </Button>
            </Box>
            {syncResult && !syncResult.error && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Sync complete: {syncResult.total_new} new, {syncResult.total_updated} updated
              </Alert>
            )}
            {syncResult?.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {syncResult.error}
              </Alert>
            )}
          </Paper>
        )}

        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Button
              variant={viewMode === 'summary' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('summary')}
              sx={{ mr: 1 }}
            >
              Summary View
            </Button>
            <Button
              variant={viewMode === 'editCategories' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('editCategories')}
            >
              Edit Categories
            </Button>
          </Box>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
          >
            Delete Account
          </Button>
        </Box>

        <CollectionSearch label="Search account categories" value={search} onChange={setSearch}
          total={viewMode === 'summary' ? categoryTotals.length : categories.length}
          shown={viewMode === 'summary' ? visibleTotals.length : visibleCategories.length} />
        {search && (viewMode === 'summary' ? visibleTotals : visibleCategories).length === 0 &&
          <Typography>No matching active categories.</Typography>}
        {viewMode === 'summary' ? (
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" component="h2">
                Category Totals
              </Typography>
              <Button 
                variant="outlined" 
                color="primary"
                onClick={() => setIsCategoryTransferModalOpen(true)}
                disabled={categories.length < 2}
                size="small"
              >
                🔄 Transfer Balances
              </Button>
            </Box>
            {categories.length < 2 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Need at least 2 categories to transfer balances
              </Typography>
            )}
            {categoryTotals.length > 0 ? (
              <List>
                {visibleTotals.map((item, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={`${item.category_name}: ${item.total_amount}`} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography>No category totals to display.</Typography>
            )}

            {/* Archived Categories Toggle */}
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Button 
                variant="outlined" 
                onClick={handleToggleArchivedCategories}
                sx={{ mb: 2 }}
              >
                {showArchivedCategories ? 'Hide' : 'Show'} Archived Categories
              </Button>

              {showArchivedCategories && (
                <Box>
                  <Typography variant="h6" component="h3" gutterBottom sx={{ color: 'text.secondary' }}>
                    📁 Archived Categories
                  </Typography>
                  {archivedCategories.length > 0 ? (
                    <List sx={{ backgroundColor: '#f5f5f5', borderRadius: 1, p: 1 }}>
                      {visibleArchived.map((category) => (
                        <ListItem 
                          key={category.id}
                          sx={{ 
                            backgroundColor: 'white', 
                            mb: 1, 
                            borderRadius: 1,
                            border: '1px solid #e0e0e0'
                          }}
                          secondaryAction={
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              onClick={() => handleReactivateCategory(category.id, category.name)}
                            >
                              Reactivate
                            </Button>
                          }
                        >
                          <ListItemText 
                            primary={
                              <Box>
                                <Typography variant="body1" component="span">
                                  {category.name}: ${category.balance}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  Archived: {category.deleted_date ? new Date(category.deleted_date).toLocaleDateString() : 'Unknown date'}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography color="text.secondary">No archived categories found.</Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Manage Categories
            </Typography>
            <Button variant="contained" onClick={handleOpenAddCategoryModal} sx={{ mb: 2 }}>
              Add New Category
            </Button>
            {categories.length > 0 ? (
              <List>
                {visibleCategories.map((category) => (
                  <ListItem
                    key={category.id}
                    secondaryAction={
                      <IconButton edge="end" aria-label="edit" onClick={() => handleOpenEditCategoryModal(category)}>
                        <EditIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={category.name} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography>No categories found for this account.</Typography>
            )}
          </Box>
        )}

        <AddCategoryModal
          open={isAddCategoryModalOpen}
          handleClose={handleCloseAddCategoryModal}
          onCategoryAdded={handleCategoryAdded}
          accountId={parseInt(id)} // Pass the current account ID to the modal
        />

        {selectedCategoryToEdit && (
          <EditCategoryModal
            open={isEditCategoryModalOpen}
            handleClose={handleCloseEditCategoryModal}
            category={selectedCategoryToEdit}
            onCategoryUpdated={handleCategoryUpdated}
          />
        )}

        {/* Delete Account Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Account</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the account "{account.name}"?
              {deleteError && (
                <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>
                  {deleteError}
                </Alert>
              )}
              {!deleteError && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Note: The system will determine whether to permanently delete (no transaction history) 
                  or archive (all balances at zero) this account.
                </Typography>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteConfirm} 
              color="error" 
              variant="contained"
              disabled={deleteLoading || deleteError}
            >
              {deleteLoading ? 'Processing...' : 'Delete Account'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Category Transfer Modal */}
        <CategoryTransferModal
          open={isCategoryTransferModalOpen}
          onClose={() => setIsCategoryTransferModalOpen(false)}
          account={account}
          categories={categories}
          onTransferComplete={fetchData}
        />
      </Box>
    </Container>
  );
}

export default AccountDetailPage;
